import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { IAuditClientItem } from "../../types/auditClientItem.type";
import {
  Button,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { api } from "../../api/api";
import { DownOutlined } from "@ant-design/icons";
import { AuditItemStatus } from "../../types/auditItem.type";

const { Title } = Typography;
const { Search } = Input;

export default function AuditClientItems() {
  const { id } = useParams();
  const [items, setItems] = useState<IAuditClientItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<AuditItemStatus | undefined>(undefined);
  const [selectedItem, setSelectedItem] = useState<
    IAuditClientItem | undefined
  >(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [form] = Form.useForm();

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/audit/${id}/client-items`, {
        params: {
          page,
          limit: pageSize,
          search: searchTerm,
          status: status,
        },
      });

      setItems(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error(error);
      message.error("Ошибка получение данных ревизии");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [page, pageSize, searchTerm, status]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleOpen = (item: IAuditClientItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
    form.setFieldsValue(item);
  };

  const handleSubmit = async (values: {
    actualTotalClientDebt: number;
    actualTotalCompanyDebt: number;
  }) => {
    setConfirmLoading(true);
    try {
      await api.post(`/audit/client-item/${selectedItem?._id}`, values);
      message.success("Данные успешно обновлены");
    } catch (error: any) {
      console.error("Error saving item:", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
      setIsModalOpen(false);
      fetchItems();
      form.resetFields();
    }
  };

  const columns = [
    {
      title: "Имя клиента",
      dataIndex: "clientName",
      key: "clientName",
    },
    {
      title: "Тип клиента",
      dataIndex: "clientType",
      key: "clientType",
    },
    {
      title: "Факт.Долг клиента",
      dataIndex: "actualTotalClientDebt",
      key: "actualTotalClientDebt",
      render: (value: number, record: IAuditClientItem) => 
        `${value.toFixed(2)} ${record.debtCurrency || "USD"}`,
    },
    {
      title: "Долг клиента",
      dataIndex: "totalClientDebt",
      key: "totalClientDebt",
      render: (value: number, record: IAuditClientItem) => 
        `${value.toFixed(2)} ${record.debtCurrency || "USD"}`,
    },
    {
      title: "Факт.Наш долг",
      key: "actualTotalCompanyDebt",
      dataIndex: "actualTotalCompanyDebt",
      render: (value: number, record: IAuditClientItem) => 
        `${value.toFixed(2)} ${record.debtCurrency || "USD"}`,
    },
    {
      title: "Наш долг",
      key: "totalCompanyDebt",
      dataIndex: "totalCompanyDebt",
      render: (value: number, record: IAuditClientItem) => 
        `${value.toFixed(2)} ${record.debtCurrency || "USD"}`,
    },
    {
      title: "Разница",
      dataIndex: "difference",
      render: (value: number, record: IAuditClientItem) => 
        `${value.toFixed(2)} ${record.debtCurrency || "USD"}`,
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, item: IAuditClientItem) => {
        const menu = (
          <Menu>
            <Menu.Item onClick={() => handleOpen(item)}>Изменить</Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const rowClassName = (record: IAuditClientItem) => {
    if (record.difference > 0) {
      return "row-green";
    } else if (record.difference < 0) {
      return "row-red";
    }
    return "";
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Данные ревизии</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />
        <Select
          placeholder="Фильтр по статусу"
          onChange={(value) => setStatus(value)}
          allowClear
          style={{ width: 200 }}
        >
          {Object.values(AuditItemStatus).map((status) => (
            <Select.Option key={status} value={status}>
              {status}
            </Select.Option>
          ))}
        </Select>
      </Space>

      <Table
        columns={columns}
        dataSource={items}
        rowKey={(record) => record._id}
        loading={loading}
        rowClassName={rowClassName}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Изменить"
        open={isModalOpen}
        confirmLoading={confirmLoading}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="actualTotalClientDebt"
            style={{ flex: 1 }}
            label={`Факт.Долг клиента ${selectedItem?.debtCurrency || "USD"}`}
          >
            <InputNumber placeholder={selectedItem?.debtCurrency || "USD"} />
          </Form.Item>

          <Form.Item
            name="totalClientDebt"
            style={{ flex: 1 }}
            label={`Долг клиента ${selectedItem?.debtCurrency || "USD"}`}
          >
            <InputNumber disabled placeholder={selectedItem?.debtCurrency || "USD"} />
          </Form.Item>

          <Form.Item
            name="actualTotalCompanyDebt"
            style={{ flex: 1 }}
            label={`Факт.Долг компании ${selectedItem?.debtCurrency || "USD"}`}
          >
            <InputNumber placeholder={selectedItem?.debtCurrency || "USD"} />
          </Form.Item>

          <Form.Item
            name="totalCompanyDebt"
            style={{ flex: 1 }}
            label={`Долг компании ${selectedItem?.debtCurrency || "USD"}`}
          >
            <InputNumber disabled placeholder={selectedItem?.debtCurrency || "USD"} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
